"use client";

// onborda types are built for React 18; cast to work with React 19

/* eslint-disable @typescript-eslint/no-explicit-any */
import { c as _c } from "react/compiler-runtime";
import { OnbordaProvider as _OnbordaProvider, useOnborda } from "onborda";
import _Onborda from "onborda/dist/Onborda";
import posthog from "posthog-js";
import React, { createContext, useCallback, useContext, useEffect, useRef } from "react";
import TourCard, { TOUR_FINISHED_EVENT } from "@/components/ui/tour-card";
import type { TourDefinition } from "@/config/tours";
const OnbordaProvider = _OnbordaProvider as React.FC<{
  children: React.ReactNode;
}>;
const Onborda = _Onborda as any as React.FC<{
  children: React.ReactNode;
  steps: TourDefinition[];
  shadowRgb?: string;
  shadowOpacity?: string;
  cardTransition?: Record<string, unknown>;
  cardComponent?: React.ComponentType<any>;
}>;

// ─────────────────────────────────────────────────────────────────────────────
// Context
// ─────────────────────────────────────────────────────────────────────────────

interface TourContextValue {
  /** Start a tour immediately (e.g. from a replay button) */
  startTour: (name: string) => void;
  /** Clear completion and restart */
  resetTour: (name: string) => void;
  isTourCompleted: (name: string) => boolean;
  /** Auto-start a tour if not already completed. Call from the target page's useEffect. */
  autoStartTour: (name: string) => void;
}
const TourContext = createContext<TourContextValue | null>(null);
export function useTour() {
  const ctx = useContext(TourContext);
  if (!ctx) {
    throw new Error("useTour must be used within a TourProvider");
  }
  return ctx;
}

// ─────────────────────────────────────────────────────────────────────────────
// localStorage helpers
// ─────────────────────────────────────────────────────────────────────────────

const STORAGE_PREFIX = "decoda-tour-completed:";

/** Window event fired with the tour name when someone clicks "Done" on its last step. Training listens for it. */
export const TOUR_COMPLETED_EVENT = "decoda:tour-completed";
function markCompleted(name: string) {
  try {
    localStorage.setItem(`${STORAGE_PREFIX}${name}`, "true");
  } catch {
    // SSR / private browsing
  }
}
function isCompleted(name: string): boolean {
  try {
    return localStorage.getItem(`${STORAGE_PREFIX}${name}`) === "true";
  } catch {
    return false;
  }
}
function clearCompleted(name: string) {
  try {
    localStorage.removeItem(`${STORAGE_PREFIX}${name}`);
  } catch {
    // SSR / private browsing
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// TourManager — lives inside OnbordaProvider so it can call useOnborda
// ─────────────────────────────────────────────────────────────────────────────

function TourManager({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    startOnborda,
    isOnbordaVisible,
    currentTour
  } = useOnborda();
  const prevVisible = useRef(isOnbordaVisible);
  // closeOnborda() clears currentTour and isOnbordaVisible in one batch,
  // so by the time the effect fires currentTour is already null.
  // Keep a ref to the last known tour name so we can mark it completed.
  const activeTourRef = useRef<string | null>(null);
  const activeTourTriggerRef = useRef<string>("unknown");
  const finishedRef = useRef(false);
  useEffect(() => {
    const onFinished = () => {
      finishedRef.current = true;
    };
    window.addEventListener(TOUR_FINISHED_EVENT, onFinished);
    return () => window.removeEventListener(TOUR_FINISHED_EVENT, onFinished);
  }, []);
  const captureTourActivity = useCallback((name: string, action: "started" | "completed", trigger: "auto" | "manual" | "reset" | "unknown") => {
    posthog.capture("tour_activity", {
      action,
      tour: name,
      trigger
    });
  }, []);
  const startTourWithTrigger = useCallback((name_0: string, trigger_0: "auto" | "manual" | "reset") => {
    activeTourRef.current = name_0;
    activeTourTriggerRef.current = trigger_0;
    captureTourActivity(name_0, "started", trigger_0);
    startOnborda(name_0);
  }, [captureTourActivity, startOnborda]);
  useEffect(() => {
    if (currentTour) activeTourRef.current = currentTour;
  }, [currentTour]);

  // Onborda only re-measures its highlight on window resize. When a step's target is
  // inside a scrolling panel (the calendar's side panel, say), its scroll-into-view moves
  // the element after the highlight was placed, leaving the highlight on empty space.
  // Content that loads late (a banner above the patient chart's tabs) moves it the same way.
  useEffect(() => {
    if (!isOnbordaVisible) return;
    let frame = 0;
    const remeasure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => window.dispatchEvent(new Event("resize")));
    };
    document.addEventListener("scroll", remeasure, {
      capture: true,
      passive: true
    });
    // The tour card re-renders on every re-measure; reacting to it would loop each frame.
    const observer = new MutationObserver(records => {
      const pageChanged = records.some(record => !(record.target instanceof Element) || !record.target.closest("[data-tour-card]"));
      if (pageChanged) remeasure();
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      document.removeEventListener("scroll", remeasure, {
        capture: true
      });
    };
  }, [isOnbordaVisible]);

  // Track visibility transitions to mark tours as completed
  useEffect(() => {
    if (prevVisible.current && !isOnbordaVisible && activeTourRef.current) {
      // Any close stops a feature tour coming back; only "Done" ticks a training task.
      markCompleted(activeTourRef.current);
      if (finishedRef.current) {
        window.dispatchEvent(new CustomEvent<string>(TOUR_COMPLETED_EVENT, {
          detail: activeTourRef.current
        }));
      }
      finishedRef.current = false;
      captureTourActivity(activeTourRef.current, "completed", activeTourTriggerRef.current as "auto" | "manual" | "reset" | "unknown");
      activeTourRef.current = null;
      activeTourTriggerRef.current = "unknown";
    }
    prevVisible.current = isOnbordaVisible;
  }, [captureTourActivity, isOnbordaVisible]);
  const startTour = useCallback((name_1: string) => {
    startTourWithTrigger(name_1, "manual");
  }, [startTourWithTrigger]);
  const resetTour = useCallback((name_2: string) => {
    clearCompleted(name_2);
    startTourWithTrigger(name_2, "reset");
  }, [startTourWithTrigger]);

  // Read inside the auto-start timer, which outlives the render that scheduled it.
  const isVisibleRef = useRef(isOnbordaVisible);
  isVisibleRef.current = isOnbordaVisible;
  const autoStartTourFn = useCallback((name_3: string) => {
    if (isCompleted(name_3)) return;
    const timer = setTimeout(() => {
      // Never take over a tour the person is in (a training "Show me", say). This one
      // stays unseen, so it shows the next time its screen opens.
      if (isVisibleRef.current) return;
      startTourWithTrigger(name_3, "auto");
    }, 800);
    return () => clearTimeout(timer);
  }, [startTourWithTrigger]);
  const isTourCompletedFn = useCallback((name_4: string) => {
    return isCompleted(name_4);
  }, []);
  return <TourContext.Provider value={{
    startTour,
    resetTour,
    isTourCompleted: isTourCompletedFn,
    autoStartTour: autoStartTourFn
  }}>
      {children}
    </TourContext.Provider>;
}

// ─────────────────────────────────────────────────────────────────────────────
// TourProvider — context-only wrapper (no DOM wrappers)
// Place high in the tree so useTour() is available everywhere.
// ─────────────────────────────────────────────────────────────────────────────

export function TourProvider(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <OnbordaProvider><TourManager>{children}</TourManager></OnbordaProvider>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}

// ─────────────────────────────────────────────────────────────────────────────
// TourOverlay — renders the Onborda spotlight + card overlay.
// Place inside a <main> or block-level container so its wrapper divs
// don't break flex/grid parents (Onborda wraps children in two block divs).
// ─────────────────────────────────────────────────────────────────────────────

export function TourOverlay(t0) {
  const $ = _c(4);
  const {
    tours
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      duration: 0.3,
      type: "tween"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span />;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== tours) {
    t3 = <Onborda steps={tours} shadowRgb="17,24,39" shadowOpacity="0.6" cardTransition={t1} cardComponent={TourCard}>{t2}</Onborda>;
    $[2] = tours;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}